import { sendEvent } from './mixpanel'

/** Which conversation list the customer was on. */
export type ConversationSource = 'myConversations' | 'companyConversations'

/**
 * The portal's Mixpanel events (see INB-4476). Names follow the tracking plan's
 * `actionHappened` camelCase with a `portal` prefix. No PII: ids only, field
 * names not values. `portalSlug` is attached to every event by initAnalytics.
 */
export const track = {
  /** A portal page was viewed; `page` is the route pattern, not the raw path. */
  portalPageVisited: (page: string) =>
    sendEvent('portalPageVisited', { portalPageVisited: page }),

  /** An OTP code was requested (initial request or resend). */
  portalCodeRequested: (result: 'success' | 'error') =>
    sendEvent('portalCodeRequested', { requestResult: result }),

  /** The OTP was verified and a session started. */
  portalSignInCompleted: () => sendEvent('portalSignInCompleted'),

  /** The OTP was rejected (or the email isn't allowed in the portal). */
  portalSignInFailed: (reason: 'invalid' | 'expired' | 'forbidden') =>
    sendEvent('portalSignInFailed', { signInFailureReason: reason }),

  /** The customer signed out from the profile menu. */
  portalSignedOut: () => sendEvent('portalSignedOut'),

  /** A conversation row was opened from a list. */
  portalConversationOpened: (source: ConversationSource) =>
    sendEvent('portalConversationOpened', { conversationSource: source }),

  /**
   * The conversation list was re-filtered: a tab switch and/or a sort change.
   * (The beta list has no status filter, so `conversationSortDir` stands in for
   * the spec's status property.)
   */
  portalConversationListFiltered: (props: {
    tab?: ConversationSource
    sortDir?: 'asc' | 'desc'
  }) =>
    sendEvent('portalConversationListFiltered', {
      conversationListTab: props.tab,
      conversationSortDir: props.sortDir,
    }),

  /** A single conversation was exported to PDF (client-side print-to-PDF). */
  portalConversationExported: () => sendEvent('portalConversationExported'),

  /** A reply was sent successfully. */
  portalReplySent: () => sendEvent('portalReplySent'),

  /** The profile was saved; `fieldsChanged` is field names only, no values. */
  portalProfileUpdated: (fieldsChanged: string[]) =>
    sendEvent('portalProfileUpdated', {
      fieldsChanged: fieldsChanged.join(','),
    }),

  /** The Beacon "New Conversation" composer was opened. */
  portalNewConversationStarted: () => sendEvent('portalNewConversationStarted'),

  /** The Beacon "Get help" answers view was opened. */
  portalHelpOpened: () => sendEvent('portalHelpOpened'),
}
